/**
 * Shared presentation rules for events so the homepage carousel, the events
 * listing and the event-details page all describe an event identically.
 *
 * Two hard rules:
 *  1. Every event states who runs it — Recovery Trauma™ or an external body.
 *  2. External events never look endorsed, organised or delivered by us.
 */
import artConference from "@/assets/events/conference-cover.jpg.asset.json";
import artWorkshop from "@/assets/events/workshop-cover.jpg.asset.json";
import artAwareness from "@/assets/events/awareness-cover.jpg.asset.json";
import artCommunity from "@/assets/events/community-cover.jpg.asset.json";
import artWorkshopB from "@/assets/events/workshop-b.jpg.asset.json";
import artWorkshopC from "@/assets/events/workshop-c.jpg.asset.json";
import artConferenceB from "@/assets/events/conference-b.jpg.asset.json";
import artConferenceC from "@/assets/events/conference-c.jpg.asset.json";
import artAwarenessB from "@/assets/events/awareness-b.jpg.asset.json";
import artAwarenessC from "@/assets/events/awareness-c.jpg.asset.json";
import artCommunityB from "@/assets/events/community-b.jpg.asset.json";
import artCommunityC from "@/assets/events/community-c.jpg.asset.json";

type EventLike = {
  category: string;
  source: "internal" | "external";
  event_image_url?: string | null;
  external_url?: string | null;
  location?: string | null;
  starts_at: string;
};

export const EXTERNAL_DISCLAIMER =
  "This event is organised by an independent external provider. Recovery Trauma™ is sharing it for information only and is not responsible for its content, booking, pricing or delivery.";

export function isExternalEvent(event: EventLike): boolean {
  return event.source === "external";
}

/** "Hosted by Recovery Trauma™" / "External Event" */
export function hostBadgeLabel(event: EventLike): string {
  return isExternalEvent(event) ? "External Event" : "Hosted by Recovery Trauma™";
}

/**
 * Cover variants per category. Each event card rotates through its category's
 * variants once per day, so the homepage looks fresh without any manual work.
 */
const COVER_VARIANTS: Record<string, string[]> = {
  awareness: [artAwareness.url, artAwarenessB.url, artAwarenessC.url],
  conference: [artConference.url, artConferenceB.url, artConferenceC.url],
  workshop: [artWorkshop.url, artWorkshopB.url, artWorkshopC.url],
  community: [artCommunity.url, artCommunityB.url, artCommunityC.url],
};

function coverGroup(category: string): string[] {
  if (category === "awareness") return COVER_VARIANTS.awareness;
  if (category === "conference" || category === "training")
    return COVER_VARIANTS.conference;
  if (
    category === "workshop" ||
    category === "journaling" ||
    category === "guest" ||
    category === "nervous"
  )
    return COVER_VARIANTS.workshop;
  return COVER_VARIANTS.community;
}

/** Whole days since the epoch in UTC — same value on server and client. */
function dayIndex(now: Date = new Date()): number {
  return Math.floor(now.getTime() / 86_400_000);
}

/** Small stable hash so two events of the same category don't show the same photo. */
function seedFrom(value: string): number {
  let h = 0;
  for (let i = 0; i < value.length; i += 1) h = (h * 31 + value.charCodeAt(i)) % 100_000;
  return h;
}

/** All cover variants available to an event (its own art first, if any). */
export function eventArtworkVariants(event: EventLike): string[] {
  if (event.event_image_url) return [event.event_image_url];
  return coverGroup(event.category);
}

/**
 * Warm Recovery Trauma™ cover photo for any event without its own art.
 * Rotates automatically each day across the category's variants.
 */
export function eventArtwork(event: EventLike, now?: Date): string {
  if (event.event_image_url) return event.event_image_url;
  const variants = coverGroup(event.category);
  const seed = seedFrom(event.starts_at ?? "");
  return variants[(dayIndex(now) + seed) % variants.length];
}

/** Plain-language noun for each event category, used in alt text. */
const CATEGORY_NOUN: Record<string, string> = {
  workshop: "live online workshop",
  journaling: "live journaling workshop",
  nervous: "nervous-system workshop",
  guest: "guest-led workshop",
  conference: "conference",
  training: "training session",
  awareness: "awareness event",
  community: "community gathering",
};

/** Scene description for each stock cover, keyed by its CDN URL. */
const COVER_SCENE: Record<string, string> = {
  [artAwareness.url]: "people gathered outdoors for a trauma awareness event",
  [artAwarenessB.url]:
    "a small group of adults walking together along a park path at golden hour",
  [artAwarenessC.url]:
    "adults standing together holding candles at dusk in a quiet park",
  [artConference.url]: "an audience listening at a conference",
  [artConferenceB.url]:
    "attendees listening attentively in a bright conference hall",
  [artConferenceC.url]:
    "people taking notes at round tables during a training seminar",
  [artWorkshop.url]: "a small workshop group seated together",
  [artWorkshopB.url]:
    "five adults sitting in a circle with notebooks in a sunlit room with plants",
  [artWorkshopC.url]:
    "four adults journaling and talking around a wooden table with mugs of tea",
  [artCommunity.url]: "a warm community gathering",
  [artCommunityB.url]:
    "six adults chatting and laughing together on sofas in a bright, plant-filled room",
  [artCommunityC.url]:
    "a community group sharing a meal and conversation at a long table",
};

/**
 * Descriptive alt text for an event cover: what the photo actually shows plus
 * which event it belongs to. Pass the same `now` used for the artwork so the
 * description always matches the day's rotated variant.
 */
export function eventArtworkAlt(
  event: EventLike & { title?: string | null },
  now?: Date,
): string {
  const title = event.title?.trim();
  const url = eventArtwork(event, now);
  const scene = COVER_SCENE[url];
  if (!scene) {
    // The event has its own artwork; describe what the card represents instead.
    const kind = CATEGORY_NOUN[event.category] ?? "live event";
    const host = isExternalEvent(event)
      ? "an external organiser"
      : "Recovery Trauma\u2122";
    return title
      ? `Cover artwork for ${title}, a ${kind} hosted by ${host}`
      : `Cover artwork for a ${kind} hosted by ${host}`;
  }
  return title
    ? `Cover photo for ${title}: ${scene}`
    : `Recovery Trauma\u2122 event cover photo showing ${scene}`;
}

/** "Online" vs a physical place, from the free-text location field. */
export function eventPlace(event: EventLike): string {
  const loc = event.location?.trim();
  if (!loc) return isExternalEvent(event) ? "See organiser's page" : "Online (Zoom)";
  return loc;
}

/** Cost, only where we actually know it. */
export function eventCost(event: EventLike): string | null {
  if (isExternalEvent(event)) return null; // pricing is set by the organiser
  return "Included with your membership";
}

/** Date + time in UK time, e.g. "Sun 16 Aug 2026 · 9:00 PM UK Time". */
export function eventWhen(iso: string): string {
  const d = new Date(iso);
  try {
    const date = new Intl.DateTimeFormat("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "Europe/London",
    }).format(d);
    const time = new Intl.DateTimeFormat("en-GB", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "Europe/London",
    })
      .format(d)
      .replace(/\bam\b/, "AM")
      .replace(/\bpm\b/, "PM");
    return `${date} · ${time} UK Time`;
  } catch {
    return d.toUTCString();
  }
}

export function eventCtaLabel(event: EventLike): string {
  return isExternalEvent(event) ? "View External Event" : "Reserve Your Place";
}

/**
 * Seasonal copy for a specific session — the reserve button and the message a
 * member sees straight after booking. Falls back to the standard wording.
 */
const SESSION_COPY: Record<
  string,
  { reserveLabel: string; bookedTitle: string; bookedNote: string }
> = {
  "pumpkin-grounding": {
    reserveLabel: "🎃 Save My Spot",
    bookedTitle: "You're booked 🎃",
    bookedNote:
      "Bring a small pumpkin or squash and find a comfortable space. We'll see you on 18 October.",
  },
};

export function eventReserveLabel(slug: string | null | undefined): string {
  return (slug && SESSION_COPY[slug]?.reserveLabel) || "Reserve My Place";
}

export function eventBookedMessage(slug: string | null | undefined): {
  title: string;
  note: string | null;
} {
  const copy = slug ? SESSION_COPY[slug] : undefined;
  return copy
    ? { title: copy.bookedTitle, note: copy.bookedNote }
    : { title: "Your place is reserved 💛", note: null };
}
